import { useMatch } from 'react-router-dom';

import { useAppShell } from '@/data/context/AppShellContext';
import usePathParamCourseProgressSelection from '@/data/hook/course-progress/usePathParamCourseProgressSelection';
import useSearchParamCourseProgressSelection from '@/data/hook/course-progress/useSearchParamCourseProgressSelection';
import { CourseProgressSelection } from '@/data/hook/student-dashboard/useStudentDashboardCourseProgresses';
import useNewUiScope from '@/data/hook/shared/useNewUiScope';
import { isStudent } from '@/functions/auth';
import { isLegacyLessonPathname } from '@/utils/lesson/legacyLessonPaths';
import { ROUTES } from '@/utils/routes';

export type ShellDesktopHeaderVariant = 'dashboard' | 'lessons';

export type ShellLessonParams = {
  lessonId: number;
  courseSlug: string;
  klassId?: string;
};

export type ShellDesktopHeaderState = {
  variant?: ShellDesktopHeaderVariant;
  lessonParams?: ShellLessonParams;
  selection: CourseProgressSelection;
};

const COURSE_PATTERN = `${ROUTES.COURSES.BASE}/:slugCourseName/*`;
const LESSON_PATTERN = `${ROUTES.COURSES.BASE}/:slugCourseName${ROUTES.LESSON.BASE}/:lessonId/*`;
const KLASS_LESSON_PATTERN = `${ROUTES.COURSES.BASE}/:slugCourseName${ROUTES.LESSON.BASE}/:lessonId${ROUTES.CLASS.BASE}/:klassId/*`;

export default function useShellDesktopHeader(): ShellDesktopHeaderState {
  const { user } = useAppShell();
  const isHome = !!useMatch({ path: ROUTES.DASHBOARD.BASE, end: true });
  const courseMatch = useMatch(COURSE_PATTERN);
  const lessonMatch = useMatch(LESSON_PATTERN);
  const klassLessonMatch = useMatch(KLASS_LESSON_PATTERN);
  const isModern = useNewUiScope(
    isHome ? 'studentDashboard' : 'studentCourseLessons',
  );
  const pathSelection = usePathParamCourseProgressSelection();
  const searchSelection = useSearchParamCourseProgressSelection();

  const lessonRouteMatch = klassLessonMatch ?? lessonMatch;
  const isLegacyLesson =
    !!lessonRouteMatch &&
    isLegacyLessonPathname(
      lessonRouteMatch.pathnameBase,
      lessonRouteMatch.pathname,
    );
  const isVisible = isStudent(user.userType) && isModern && !isLegacyLesson;

  const variant = getVariant({ isHome, isCourse: !!courseMatch });

  return {
    variant: isVisible ? variant : undefined,
    lessonParams: toLessonParams(lessonRouteMatch?.params),
    selection: courseMatch ? pathSelection : searchSelection,
  };
}

type LessonRouteParams = {
  slugCourseName?: string;
  lessonId?: string;
  klassId?: string;
};

const toLessonParams = (
  params?: LessonRouteParams,
): ShellLessonParams | undefined => {
  const lessonId = Number(params?.lessonId);

  if (!lessonId || !params?.slugCourseName) return undefined;

  return {
    lessonId,
    courseSlug: params.slugCourseName,
    klassId: params.klassId,
  };
};

type GetVariantParams = {
  isHome: boolean;
  isCourse: boolean;
};

const getVariant = ({
  isHome,
  isCourse,
}: GetVariantParams): ShellDesktopHeaderVariant | undefined => {
  if (isHome) return 'dashboard';
  if (isCourse) return 'lessons';
  return undefined;
};
